import React, { useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  InputAdornment,
  TextField,
  Typography,
} from "@material-ui/core";
import { Add, Replay } from "@material-ui/icons";
import { useDispatch } from "react-redux";

import KPTable from "../../../components/KPTable";
import { apiCall } from "../../../utils/api";
import { API_URLS } from "../../../constants/api";
import { notification } from "../../../reducer/home/action";

// Admin form for the key-value reward_global_configs table.
//
// Known keys (seeded by the BE) get human-readable labels and explicit unit hints.
// Unknown keys still appear and are inline-editable as raw key/value/note triples —
// this future-proofs the UI: when the BE adds a new key, admins don't need a code
// change to manage it.
//
// All values stored as int64. For currency keys (e.g. MAX_REWARD_PER_YEAR) the
// formatted VND amount is shown alongside the raw input.

const KNOWN_KEYS = {
  MAX_REWARD_PER_YEAR: {
    label: "Hạn mức thưởng tối đa / cá nhân / năm",
    unit: "VND",
    description:
      "Tổng tiền (đã nhận + chờ nhận) trong 1 năm của 1 cá nhân. Hệ thống chặn duyệt khi vượt hạn mức này.",
    isCurrency: true,
  },
  STUDENT_DISCOUNT_PCT: {
    label: "% giảm tiền thưởng cho sinh viên",
    unit: "%",
    description:
      "Sau khi tính share theo vai trò (tác giả chính / đồng tác giả), tiền thưởng của tác giả là sinh viên bị giảm theo phần trăm này. Lưu nguyên: 50 = 50%.",
    isCurrency: false,
  },
};

const formatVnd = (n) => (n || 0).toLocaleString("vi-VN") + " ₫";

function RewardGlobalConfigForm() {
  const dispatch = useDispatch();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editting, setEditting] = useState(null); // inline-edit row state
  const [creating, setCreating] = useState(null); // dialog state for adding a new key

  const load = async () => {
    setLoading(true);
    const { response, error } = await apiCall({ ...API_URLS.REWARD_GLOBAL_CONFIG.getAll() });
    if (!error && response?.status === 200 && response.data?.success) {
      setRows(response.data.data || []);
    } else {
      dispatch(notification({ type: "error", message: "Không tải được cấu hình" }));
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const handleSubmitInline = async () => {
    if (!editting) return;
    const value = parseInt(editting.value, 10);
    if (Number.isNaN(value) || value < 0) {
      dispatch(notification({ type: "error", message: "Giá trị phải là số nguyên >= 0" }));
      return;
    }
    const { response, error } = await apiCall({
      ...API_URLS.REWARD_GLOBAL_CONFIG.upsert(),
      payload: { key: editting.key, value, note: editting.note || "" },
    });
    if (!error && response?.status === 200 && response.data?.success) {
      dispatch(notification({ type: "success", message: `Đã lưu ${editting.key}` }));
      setEditting(null);
      load();
    } else {
      dispatch(notification({ type: "error", message: response?.data?.message || "Lưu thất bại" }));
    }
  };

  const handleSubmitCreate = async () => {
    if (!creating) return;
    if (!creating.key?.trim()) {
      dispatch(notification({ type: "error", message: "Key không được trống" }));
      return;
    }
    if (!/^[A-Z0-9_]+$/.test(creating.key)) {
      dispatch(notification({ type: "error", message: "Key chỉ gồm chữ IN HOA, số và dấu _" }));
      return;
    }
    const value = parseInt(creating.value, 10);
    if (Number.isNaN(value) || value < 0) {
      dispatch(notification({ type: "error", message: "Giá trị phải là số nguyên >= 0" }));
      return;
    }
    const { response, error } = await apiCall({
      ...API_URLS.REWARD_GLOBAL_CONFIG.upsert(),
      payload: { key: creating.key.trim().toUpperCase(), value, note: creating.note || "" },
    });
    if (!error && response?.status === 200 && response.data?.success) {
      dispatch(notification({ type: "success", message: "Đã thêm" }));
      setCreating(null);
      load();
    } else {
      dispatch(notification({ type: "error", message: response?.data?.message || "Lưu thất bại" }));
    }
  };

  return (
    <>
      <Grid container>
        <Grid item xs={6}>
          <Typography variant="h5" style={{ lineHeight: "36px" }}>
            Tham số thưởng (cap, hạn mức)
          </Typography>
        </Grid>
        <Grid item xs={6}>
          <div style={{ float: "right" }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<Add />}
              onClick={() => setCreating({ key: "", value: 0, note: "" })}
              style={{ marginRight: 8 }}
            >
              Thêm
            </Button>
            <Button variant="outlined" startIcon={<Replay />} onClick={load}>
              Tải lại
            </Button>
          </div>
        </Grid>
      </Grid>
      <Divider style={{ margin: "8px 0" }} />

      <KPTable
        shadow="none"
        loading={loading}
        rowsPerPage={20}
        maxHeight="calc(100vh - 288px)"
        columns={[
          {
            title: <b>Key</b>,
            key: "key",
            width: "20%",
            searchable: true,
            render: (value) => <code style={{ fontSize: 12, color: "#444" }}>{value}</code>,
          },
          {
            title: <b>Mô tả</b>,
            key: "_label",
            width: "30%",
            render: (_, row) => {
              const meta = KNOWN_KEYS[row.key];
              return (
                <>
                  <Typography variant="body2" style={{ fontWeight: 600 }}>
                    {meta?.label || row.key}
                  </Typography>
                  {meta?.description && (
                    <Typography variant="caption" style={{ color: "#888" }}>
                      {meta.description}
                    </Typography>
                  )}
                </>
              );
            },
          },
          {
            title: <b>Giá trị</b>,
            key: "value",
            width: "20%",
            sorter: (a, b) => (a.value || 0) - (b.value || 0),
            render: (value, row) => {
              const meta = KNOWN_KEYS[row.key] || {};
              if (editting?.key === row.key) {
                return (
                  <>
                    <TextField
                      autoFocus
                      fullWidth
                      size="small"
                      type="number"
                      variant="outlined"
                      value={editting.value}
                      onChange={(e) => setEditting({ ...editting, value: e.target.value })}
                      InputProps={{
                        endAdornment: meta.unit ? (
                          <InputAdornment position="end">{meta.unit}</InputAdornment>
                        ) : null,
                      }}
                    />
                    {meta.isCurrency && (
                      <Typography variant="caption" style={{ color: "#1976d2" }}>
                        {formatVnd(parseInt(editting.value, 10) || 0)}
                      </Typography>
                    )}
                  </>
                );
              }
              return (
                <>
                  <span style={{ color: "#1976d2", fontWeight: 600 }}>
                    {(value || 0).toLocaleString("vi-VN")} {meta.unit || ""}
                  </span>
                  {meta.isCurrency && (
                    <Typography variant="caption" style={{ display: "block", color: "#888" }}>
                      = {formatVnd(value)}
                    </Typography>
                  )}
                </>
              );
            },
          },
          {
            title: <b>Ghi chú</b>,
            key: "note",
            width: "20%",
            render: (value, row) =>
              editting?.key === row.key ? (
                <TextField
                  fullWidth
                  size="small"
                  variant="outlined"
                  value={editting.note || ""}
                  onChange={(e) => setEditting({ ...editting, note: e.target.value })}
                />
              ) : (
                value || ""
              ),
          },
          {
            title: <b>Tác vụ</b>,
            key: "action",
            width: "10%",
            render: (_, row) =>
              editting?.key === row.key ? (
                <>
                  <Typography
                    className="table-action-buton"
                    variant="caption"
                    color="primary"
                    onClick={handleSubmitInline}
                  >
                    Lưu
                  </Typography>
                  <Typography
                    className="table-action-buton"
                    variant="caption"
                    color="secondary"
                    onClick={() => setEditting(null)}
                    style={{ marginLeft: 8 }}
                  >
                    Huỷ
                  </Typography>
                </>
              ) : (
                <Typography
                  className="table-action-buton"
                  variant="caption"
                  color="primary"
                  onClick={() => setEditting({ ...row })}
                >
                  Sửa
                </Typography>
              ),
          },
        ]}
        rows={rows}
      />

      {/* Add new key dialog */}
      <Dialog open={!!creating} onClose={() => setCreating(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Thêm tham số thưởng</DialogTitle>
        <DialogContent dividers>
          <TextField
            fullWidth
            size="small"
            variant="outlined"
            margin="dense"
            label="Key (chữ IN HOA, dấu _)"
            placeholder="VD: MAX_REWARD_PER_PAPER"
            value={creating?.key || ""}
            onChange={(e) => setCreating({ ...creating, key: e.target.value.toUpperCase() })}
          />
          <TextField
            fullWidth
            size="small"
            variant="outlined"
            margin="dense"
            type="number"
            label="Giá trị"
            value={creating?.value ?? 0}
            onChange={(e) => setCreating({ ...creating, value: e.target.value })}
          />
          <TextField
            fullWidth
            size="small"
            variant="outlined"
            margin="dense"
            label="Ghi chú"
            value={creating?.note || ""}
            onChange={(e) => setCreating({ ...creating, note: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreating(null)}>Huỷ</Button>
          <Button onClick={handleSubmitCreate} color="primary" variant="contained">
            Lưu
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default RewardGlobalConfigForm;
